import { fitFixedDurationInBounds } from '@app/core/range';
import { roundToSeconds } from '@mi-front/core';
import { MAX_STREAM_TO_LOOKBACK_MS, STREAM_RANGE_DURATION_MS } from '../consts';

/** Input for computing default stream range from URL/init state. */
export interface LoggerRangeConfig {
  readonly cursor?: number | null;
  /** When set and cursor is missing, range is centered on selection.from (same as cursor). */
  readonly selection?: { readonly from: number; readonly to: number } | null;
}

/** Half-window offset: range start is this far before cursor / selection.from (30 min). */
export const CURSOR_OFFSET = STREAM_RANGE_DURATION_MS / 2;

const MINUTE_MS = 60 * 1000;

function getDefaultRangeStart(nowMs: number): number {
  const rawStart = nowMs - STREAM_RANGE_DURATION_MS - MAX_STREAM_TO_LOOKBACK_MS;
  const startOfMinute = Math.floor(rawStart / MINUTE_MS) * MINUTE_MS;
  return roundToSeconds(startOfMinute);
}

/**
 * Computes a 1-hour [from, to] range for the stream.
 * - If cursor is set: range is one hour centered on cursor.
 * - Else if selection is set: range is one hour centered on selection.from.
 * - Otherwise: range is the last hour up to `now`.
 * - If the computed `to` would exceed `now - MAX_STREAM_TO_LOOKBACK_MS`, the window is
 *   shifted back to `[maxAllowedTo - 1h, maxAllowedTo]`.
 *
 * @param now - Reference instant (e.g. `new Date()`); used for the default range and for capping `to`.
 */
export function calculateRangeFromConfig(config: LoggerRangeConfig, now: Date): [number, number] {
  const { cursor, selection } = config;
  // Prefer explicit cursor when present; otherwise fall back to selection start.
  const anchorTs = cursor ?? selection?.from;

  // The stream window cannot end inside the "too recent" zone.
  const maxAllowedTo = now.getTime() - MAX_STREAM_TO_LOOKBACK_MS;

  // For anchored loads, place the 1h window around the anchor (30m before it).
  // For default loads, derive a recent rounded start based on current time.
  const targetFrom =
    anchorTs != null
      ? roundToSeconds(anchorTs - CURSOR_OFFSET)
      : getDefaultRangeStart(now.getTime());

  // Keep a fixed 1-hour window fully enclosed within the allowed upper bound.
  const { from, to } = fitFixedDurationInBounds({
    anchorTs: targetFrom,
    durationMs: STREAM_RANGE_DURATION_MS,
    bounds: { from: Number.MIN_SAFE_INTEGER, to: maxAllowedTo },
    anchor: 'start',
  });

  // Return tuple shape expected by stream initialization callers.
  return [from, to];
}
